import type { MessageKey } from '@dijitalapp/i18n';
import { MUTE_PRESETS, type ContactDto, type ConversationDto, type MessageDto } from '@dijitalapp/shared';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { NavLink, useNavigate, useParams } from 'react-router';
import { Icon } from '../components/Icon';
import { ReportDialog, type ReportTarget } from '../components/ReportDialog';
import { api, ApiError, errorMessageKey } from '../lib/api';
import { useApp } from '../lib/app-context';
import { useChat, useChatStore } from '../lib/chat-context';
import type { PendingMessage } from '../lib/chat-store';
import { useShell } from './Shell';

const CONNECTION_LABEL: Record<string, MessageKey | null> = {
  connecting: 'conn.connecting',
  reconnecting: 'conn.reconnecting',
  offline: 'conn.offline',
  connected: null,
};

function initials(name: string): string {
  return name.trim().slice(0, 1).toUpperCase() || '?';
}

function sameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function useDayLabel() {
  const { i18n } = useApp();
  return (iso: string) => {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(today.getTime() - 86_400_000);
    if (sameDay(d, today)) return i18n.t('chat.today');
    if (sameDay(d, yesterday)) return i18n.t('chat.yesterday');
    return i18n.formatDate(d, { dateStyle: 'long' });
  };
}

function shortTime(i18n: ReturnType<typeof useApp>['i18n'], iso: string) {
  const d = new Date(iso);
  return sameDay(d, new Date()) ? i18n.formatDate(d, { timeStyle: 'short' }) : i18n.formatDate(d, { dateStyle: 'short' });
}

/** Chat list shown in the shell's list pane. */
export function ConversationList() {
  const { i18n } = useApp();
  const { t } = i18n;
  const chat = useChat();
  const [query, setQuery] = useState('');
  const label = CONNECTION_LABEL[chat.connection];
  const q = query.trim().toLowerCase();
  const list = q
    ? chat.conversations.filter((c) => `${c.peer.displayName} ${c.peer.username}`.toLowerCase().includes(q))
    : chat.conversations;

  return (
    <div className="conversation-list">
      <div className="pane-header">
        <label className="visually-hidden" htmlFor="chat-search">
          {t('chat.search')}
        </label>
        <input
          id="chat-search"
          className="input"
          type="search"
          placeholder={t('chat.search')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <NavLink to="/messages/new" className="btn" aria-label={t('chat.new')} title={t('chat.new')}>
          <Icon name="plus" />
        </NavLink>
      </div>
      {label && (
        <p className="connection-pill" role="status">
          {t(label)}
        </p>
      )}
      {chat.loaded && list.length === 0 && <p className="muted" style={{ padding: 'var(--space-4)' }}>{t('chat.empty')}</p>}
      <ul className="plain-list">
        {list.map((c) => (
          <li key={c.id}>
            <NavLink to={`/messages/${c.id}`} className="list-item conversation-item">
              <span className="avatar" aria-hidden>
                {initials(c.peer.displayName)}
              </span>
              <span className="conversation-text">
                <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
                  <strong className="truncate">{c.peer.displayName}</strong>
                  <span className="spacer" />
                  {c.lastMessage && <span className="muted nowrap">{shortTime(i18n, c.lastMessage.createdAt)}</span>}
                </span>
                <span className="row" style={{ gap: 'var(--space-2)', flexWrap: 'nowrap' }}>
                  <span className="muted truncate">
                    {chat.typing[c.id]
                      ? t('chat.typing', { name: c.peer.displayName })
                      : c.lastMessage
                        ? `${c.lastMessage.senderId === c.peer.id ? '' : `${t('chat.you')}: `}${c.lastMessage.body ?? ''}`
                        : `@${c.peer.username}`}
                  </span>
                  <span className="spacer" />
                  {c.unreadCount > 0 && (
                    <span className="unread-badge" aria-label={t('chat.unread', { count: c.unreadCount })}>
                      {c.unreadCount}
                    </span>
                  )}
                </span>
              </span>
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** /messages: nothing selected — contacts and starting a chat. */
export function MessagesHome() {
  const { i18n } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const navigate = useNavigate();
  const { setMobileView } = useShell();
  const [contacts, setContacts] = useState<ContactDto[]>([]);
  const [username, setUsername] = useState('');
  const [status, setStatus] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  const reload = () => api<{ contacts: ContactDto[] }>('GET', '/contacts').then((r) => setContacts(r.contacts));
  useEffect(() => {
    void api<{ contacts: ContactDto[] }>('GET', '/contacts').then((r) => setContacts(r.contacts));
  }, []);

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setStatus(null);
    try {
      await fn();
      if (ok) setStatus({ kind: 'ok', text: ok });
    } catch (err) {
      setStatus({ kind: 'error', text: t(errorMessageKey(err)) });
    }
  };

  const add = (e: FormEvent) => {
    e.preventDefault();
    void act(async () => {
      const c = await api<ContactDto>('POST', '/contacts', { username });
      setUsername('');
      await reload();
      setStatus({ kind: 'ok', text: t('contacts.added', { name: c.displayName }) });
    });
  };

  const message = (c: ContactDto) =>
    act(async () => {
      if (!store) return;
      const conv = await store.startDirect(c.id);
      navigate(`/messages/${conv.id}`);
    });

  return (
    <div className="content-inner">
      <button className="btn mobile-only" type="button" onClick={() => setMobileView('list')}>
        <Icon name="back" label={t('chat.back')} />
      </button>
      <div className="empty-state" style={{ paddingBlock: 'var(--space-5)' }}>
        <Icon name="messages" />
        <h1>{t('chat.select')}</h1>
        <p>{t('chat.selectBody')}</p>
      </div>
      <section className="card stack" aria-labelledby="contacts-title">
        <h2 id="contacts-title">{t('contacts.title')}</h2>
        <form className="row" onSubmit={add} style={{ flexWrap: 'nowrap' }}>
          <label className="visually-hidden" htmlFor="contact-username">
            {t('contacts.addLabel')}
          </label>
          <input
            id="contact-username"
            className="input"
            autoCapitalize="none"
            autoComplete="off"
            placeholder={t('contacts.usernamePlaceholder')}
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
          <button className="btn btn-primary">{t('contacts.add')}</button>
        </form>
        <p className="hint">{t('contacts.hint')}</p>
        {status && (
          <p role="status" className={status.kind === 'error' ? 'alert alert-error' : 'muted'}>
            {status.text}
          </p>
        )}
        {contacts.length === 0 && <p className="muted">{t('contacts.empty')}</p>}
        {contacts.map((c) => (
          <div key={c.id} className="toggle-row">
            <span className="avatar" aria-hidden>
              {initials(c.displayName)}
            </span>
            <div style={{ minWidth: 0 }}>
              <strong>{c.displayName}</strong> {c.blocked && <span className="badge">{t('contacts.blocked')}</span>}
              <p className="muted">@{c.username}</p>
            </div>
            <span className="spacer" />
            {!c.blocked && (
              <button className="btn btn-primary" type="button" onClick={() => void message(c)}>
                {t('contacts.message')}
              </button>
            )}
            <button
              className="btn btn-danger"
              type="button"
              onClick={() =>
                void act(async () => {
                  if (c.blocked) await api('DELETE', `/blocks/${c.id}`);
                  else await api('POST', '/blocks', { userId: c.id });
                  await reload();
                })
              }
            >
              {c.blocked ? t('contacts.unblock') : t('contacts.block')}
            </button>
          </div>
        ))}
      </section>
    </div>
  );
}

type Row =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'message'; key: string; message: MessageDto; mine: boolean }
  | { kind: 'pending'; key: string; pending: PendingMessage };

function deliveryStatus(m: MessageDto, c: ConversationDto): 'sent' | 'delivered' | 'read' {
  if (m.seq <= c.peerLastReadSeq) return 'read';
  if (m.seq <= c.peerLastDeliveredSeq) return 'delivered';
  return 'sent';
}

const TICKS: Record<string, string> = { sent: '✓', delivered: '✓✓', read: '✓✓', queued: '🕓', sending: '🕓', failed: '!' };

export function ConversationView() {
  const { id = '' } = useParams();
  const { i18n, me, config } = useApp();
  const { t } = i18n;
  const store = useChatStore();
  const chat = useChat();
  const { setMobileView } = useShell();
  const dayLabel = useDayLabel();
  const conversation = chat.conversations.find((c) => c.id === id);
  const messages = chat.messages[id];
  const pending = chat.pending[id] ?? [];
  const scroller = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draft, setDraft] = useState('');
  const [report, setReport] = useState<{ target: ReportTarget; evidence: string[] } | null>(null);

  const reportMessage = (m: MessageDto) => {
    // Offer up to five earlier messages as optional context; the reported one is always included.
    const list = messages ?? [];
    const index = list.findIndex((x) => x.id === m.id);
    setReport({ target: { type: 'message', messageId: m.id }, evidence: list.slice(Math.max(0, index - 5), index).map((x) => x.id) });
  };

  const mute = async (value: string) => {
    await api('PUT', `/conversations/${id}/mute`, { duration: value === 'off' ? null : value });
    await store?.refreshConversation(id);
  };

  useEffect(() => {
    if (!store) return;
    store.setActive(id);
    stickToBottom.current = true;
    void store.openConversation(id).catch(() => undefined);
    const onVisible = () => store.markRead(id);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      store.setActive(null, id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [store, id]);

  const lastSeq = conversation?.lastSeq ?? 0;
  useEffect(() => {
    store?.markRead(id);
  }, [store, id, lastSeq]);

  const rowCount = (messages?.length ?? 0) + pending.length;
  useLayoutEffect(() => {
    const el = scroller.current;
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight;
  }, [rowCount, id]);

  const rows: Row[] = [];
  let lastDay = '';
  for (const m of messages ?? []) {
    const day = new Date(m.createdAt).toDateString();
    if (day !== lastDay) rows.push({ kind: 'day', key: `d-${day}`, label: dayLabel(m.createdAt) });
    lastDay = day;
    rows.push({ kind: 'message', key: m.id, message: m, mine: m.senderId === me?.id });
  }
  for (const p of pending) rows.push({ kind: 'pending', key: p.clientMsgId, pending: p });

  const blockedReason: MessageKey | null = !me?.emailVerified
    ? 'chat.verifyToSend'
    : me.accountState !== 'active'
      ? 'chat.restrictedToSend'
      : conversation && !conversation.canSend
        ? conversation.lastMessage
          ? 'chat.cannotSend'
          : 'chat.waitForReply'
        : null;

  const submit = (e?: FormEvent) => {
    e?.preventDefault();
    const body = draft.trim();
    if (!body || !store || blockedReason) return;
    stickToBottom.current = true;
    setDraft('');
    store.setTyping(id, false);
    void store.send(id, body);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      submit();
    }
  };

  const onDraft = (value: string) => {
    setDraft(value);
    if (!store) return;
    store.setTyping(id, value.length > 0);
    if (typingTimer.current) clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => store.setTyping(id, false), 3000);
  };

  if (!conversation) {
    return chat.loaded ? (
      <div className="empty-state" role="status">
        <p>{t('error.NOT_FOUND')}</p>
      </div>
    ) : (
      <p className="muted">{t('app.loading')}</p>
    );
  }

  return (
    <div className="conversation">
      <header className="conversation-header">
        <button className="btn mobile-only" type="button" onClick={() => setMobileView('list')}>
          <Icon name="back" label={t('chat.back')} />
        </button>
        <span className="avatar" aria-hidden>
          {initials(conversation.peer.displayName)}
        </span>
        <div style={{ minWidth: 0 }}>
          <h1 className="truncate" style={{ fontSize: 'var(--text-md)' }}>
            {conversation.peer.displayName}
          </h1>
          <p className="muted truncate" aria-live="polite">
            {chat.typing[id] ? t('chat.typing', { name: conversation.peer.displayName }) : `@${conversation.peer.username}`}
          </p>
        </div>
        <span className="spacer" />
        <label className="visually-hidden" htmlFor="mute">
          {t('mute.label')}
        </label>
        <select
          id="mute"
          className="input header-select"
          value=""
          onChange={(e) => void mute(e.target.value).catch(() => undefined)}
        >
          <option value="" disabled>
            {conversation.mutedUntil ? `🔕 ${t('mute.active')}` : t('mute.label')}
          </option>
          {MUTE_PRESETS.map((p) => (
            <option key={p} value={p}>
              {t(`mute.${p}` as MessageKey)}
            </option>
          ))}
          {conversation.mutedUntil && <option value="off">{t('mute.off')}</option>}
        </select>
        <button
          className="btn btn-danger"
          type="button"
          onClick={() =>
            setReport({
              target: { type: 'user', userId: conversation.peer.id },
              evidence: (messages ?? []).filter((m) => m.senderId === conversation.peer.id).slice(-10).map((m) => m.id),
            })
          }
        >
          {t('report.user')}
        </button>
      </header>
      {report && <ReportDialog target={report.target} recentMessageIds={report.evidence} onClose={() => setReport(null)} />}

      <div
        className="message-scroller"
        ref={scroller}
        onScroll={(e) => {
          const el = e.currentTarget;
          stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
        }}
      >
        <p className="encryption-notice">{t('chat.encryptionNotice')}</p>
        {chat.hasMore[id] && (
          <button className="btn" type="button" style={{ alignSelf: 'center' }} onClick={() => void store?.loadOlder(id)}>
            {t('chat.loadOlder')}
          </button>
        )}
        {messages && messages.length === 0 && pending.length === 0 && <p className="muted center">{t('chat.noMessages')}</p>}
        <ol className="message-list" aria-label={conversation.peer.displayName}>
          {rows.map((r) => {
            if (r.kind === 'day') {
              return (
                <li key={r.key} className="day-separator">
                  <span>{r.label}</span>
                </li>
              );
            }
            const mine = r.kind === 'pending' || r.mine;
            const body = r.kind === 'pending' ? r.pending.body : r.message.body;
            const time = r.kind === 'pending' ? r.pending.createdAt : r.message.createdAt;
            const status = r.kind === 'pending' ? r.pending.status : mine ? deliveryStatus(r.message, conversation) : null;
            return (
              <li key={r.key} className={`bubble-row ${mine ? 'mine' : 'theirs'}`}>
                <div className={`bubble ${status === 'failed' ? 'bubble-failed' : ''}`}>
                  <p className="bubble-text">{body}</p>
                  <span className="bubble-meta">
                    {i18n.formatDate(time, { timeStyle: 'short' })}
                    {status && (
                      <span className={`tick tick-${status}`} title={t(`chat.status.${status}` as MessageKey)}>
                        <span aria-hidden>{TICKS[status]}</span>
                        <span className="visually-hidden">{t(`chat.status.${status}` as MessageKey)}</span>
                      </span>
                    )}
                  </span>
                  {r.kind === 'message' && !r.mine && (
                    <button className="link-button report-link" type="button" onClick={() => reportMessage(r.message)}>
                      {t('report.message')}
                    </button>
                  )}
                  {r.kind === 'pending' && r.pending.status === 'failed' && (
                    <span className="row" style={{ gap: 'var(--space-2)' }}>
                      <span className="hint">{t(errorMessageKey(new ApiError(r.pending.error ?? 'UNKNOWN', 0)))}</span>
                      <button className="btn" type="button" onClick={() => void store?.retry(r.pending)}>
                        {t('chat.retry')}
                      </button>
                      <button className="btn btn-danger" type="button" onClick={() => store?.discard(r.pending)}>
                        {t('chat.discard')}
                      </button>
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </div>

      {blockedReason ? (
        <p className="composer-blocked" role="status">
          {t(blockedReason)}
        </p>
      ) : (
        <form className="composer" onSubmit={submit}>
          <label className="visually-hidden" htmlFor="composer">
            {t('chat.placeholder')}
          </label>
          <textarea
            id="composer"
            className="input composer-input"
            rows={1}
            placeholder={t('chat.placeholder')}
            value={draft}
            maxLength={config?.remoteConfig.maxMessageLength}
            onChange={(e) => onDraft(e.target.value)}
            onKeyDown={onKeyDown}
          />
          <button className="btn btn-primary" disabled={!draft.trim()}>
            {t('chat.send')}
          </button>
        </form>
      )}
    </div>
  );
}
